import { useQuery } from "@tanstack/react-query";
import { getSupabaseWithLogging } from "@/lib/hooks/api/utils";
import { buildIlikeOrFilter } from "@west-tennessee-consulting/supabase/postgrest-filter";

/**
 * Fetch paginated list of companies
 */
export function useCompaniesPaginated(params?: {
  page?: number;
  limit?: number;
  search?: string;
  orderBy?: "name" | "created_at";
  orderDirection?: "asc" | "desc";
}) {
  return useQuery({
    queryKey: ["companies", params],
    queryFn: async () => {
      const supabase = getSupabaseWithLogging();
      let query = supabase.from("companies").select("*", { count: "exact" });

      // Apply search filter
      if (params?.search) {
        // User text is sanitized: commas/parens would otherwise rewrite the PostgREST filter.
        const searchFilter = buildIlikeOrFilter(["name", "normalized_name"], params?.search);
        if (searchFilter) query = query.or(searchFilter);
      }

      // Apply ordering
      const orderBy = params?.orderBy || "created_at";
      const orderDirection = params?.orderDirection || "desc";
      query = query.order(orderBy, { ascending: orderDirection === "asc" });

      // Apply pagination
      if (params?.limit) {
        const offset = (params.page || 0) * params.limit;
        query = query.range(offset, offset + params.limit - 1);
      }

      const { data, error, count } = await query;

      if (error) {
        console.error("Error fetching companies:", error);
        throw new Error(error.message);
      }

      return {
        data: data || [],
        count: count || 0,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch a single company by ID
 */
export function useCompany(
  companyId: string | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["company", companyId],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const supabase = getSupabaseWithLogging();
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .eq("id", companyId)
        .single();

      if (error) {
        console.error("Error fetching company:", error);
        throw new Error(error.message);
      }

      return data;
    },
    enabled: (options?.enabled !== false) && !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch all tickets for a company
 * Uses company_id as the primary filter for direct company-to-ticket relationship
 */
export function useCompanyTickets(
  companyId: string | null | undefined,
  params?: {
    status?: string;
    limit?: number;
  }
) {
  return useQuery({
    queryKey: ["company-tickets", companyId, params],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const supabase = getSupabaseWithLogging();
      try {
        let query = supabase
          .from("tickets")
          .select("*") // Select all fields - Supabase will handle RLS
          .eq("company_id", companyId); // Primary filter: company_id

        if (params?.status) {
          query = query.eq("status", params.status);
        }

        if (params?.limit) {
          query = query.limit(params.limit);
        }

        const { data, error } = await query.order("created_at", {
          ascending: false,
        });

        if (error) {
          console.error("Error fetching company tickets:", error);
          throw new Error(error.message);
        }

        return data || [];
      } catch (err) {
        console.error("Exception in useCompanyTickets:", err);
        throw err;
      }
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch all contacts for a company
 */
export function useCompanyContacts(
  companyId: string | null | undefined,
  params?: {
    limit?: number;
  }
) {
  return useQuery({
    queryKey: ["company-contacts", companyId, params],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const supabase = getSupabaseWithLogging();
      try {
        console.log(`[useCompanyContacts] Fetching contacts for company: ${companyId}`);

        let query = supabase
          .from("contacts")
          .select("*") // Select all fields - Supabase will handle RLS
          .eq("company_id", companyId);

        if (params?.limit) {
          query = query.limit(params.limit);
        }

        const { data, error } = await query.order("created_at", {
          ascending: false,
        });

        if (error) {
          console.error("[useCompanyContacts] Supabase error:", {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint,
          });
          throw new Error(`Failed to fetch contacts: ${error.message}`);
        }

        console.log(`[useCompanyContacts] Successfully fetched ${data?.length || 0} contacts`);
        return data || [];
      } catch (err) {
        console.error("[useCompanyContacts] Exception:", err);
        throw err;
      }
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
